/**
 * ai-tracking-page.tsx — Nhân viên AI › "Báo cáo và theo dõi" (`/ai/theo-doi`). Tab chọn qua hash:
 * `#nhat-ky` Nhật ký trả lời · `#cau-hoi` Câu hỏi AI chưa trả lời được · `#kiem-dinh` Kiểm định · `#thong-ke` Thống kê sử dụng.
 */
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/shared/page-header'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { TRACKING_TABS, trackingTabFromHash, type TrackingTab } from '@/lib/ai-khung'
import { useAiCredits, useAiCreditsSocket } from '@/hooks/use-ai-credits'
import { AiCreditChip } from './credits/credit-widgets'
import { BuyCreditsDialog } from './credits/buy-credits-dialog'
import { AiEvalTab } from './ai-eval-tab'
import { KnowledgeGapsPage } from './knowledge-gaps-page'
import { AiLogsTab } from './tracking/ai-logs-tab'
import { AiUsageTab } from './tracking/ai-usage-tab'
import { useAiPerms } from './use-ai-perms'

export function AiTrackingPage() {
  const { t } = useTranslation('aiOps')
  // Quản lý bộ kiểm định (ai-eval-routes) đòi `ai.update` — không có quyền thì tab chỉ xem.
  const { canEdit } = useAiPerms()
  const { hash, pathname, search } = useLocation()
  const navigate = useNavigate()
  // "Câu hỏi AI chưa trả lời được": GET /ai/knowledge-gaps đòi `ai.update` (knowledge-gap-routes.ts) → người chỉ xem không thấy tab.
  const tabs = TRACKING_TABS.filter((tb) => tb.id !== 'cau-hoi' || canEdit)
  const fromHash = trackingTabFromHash(hash)
  const tab = tabs.some((tb) => tb.id === fromHash) ? fromHash : 'nhat-ky'
  const [buyOpen, setBuyOpen] = useState(false)
  const { data: credits } = useAiCredits()
  useAiCreditsSocket()

  const setTab = (id: string) => navigate({ pathname, search, hash: `#${id as TrackingTab}` }, { replace: true })

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('trackingPage.title')}
        description={t('trackingPage.description')}
        actions={credits ? <AiCreditChip credits={credits} onBuy={() => setBuyOpen(true)} /> : undefined}
      />
      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          {tabs.map((tb) => <TabsTrigger key={tb.id} value={tb.id}>{tb.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="nhat-ky"><AiLogsTab /></TabsContent>
        {canEdit && <TabsContent value="cau-hoi"><KnowledgeGapsPage embedded /></TabsContent>}
        <TabsContent value="kiem-dinh"><AiEvalTab canEdit={canEdit} /></TabsContent>
        <TabsContent value="thong-ke"><AiUsageTab onBuy={() => setBuyOpen(true)} /></TabsContent>
      </Tabs>
    </div>
  )
}
